// src/components/admin/BookingLeaveManagement.jsx
// Updated: adds Pending Approvals section for managers/admins/owners
import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { getSalonStaff } from '../../api/salonApi';
import { useSalonAuth } from '../../contexts/SalonAuthContext';
import StaffLeaveManager from './StaffLeaveManager';
import '../../styles/staffeditor.css';
import '../../styles/staff-portal.css';

// onConflictResolved(leaveDocId) — optional, forwarded from Admin.jsx so that resolving
//   a conflict in the Leave Records page also dismisses it from the notifications modal.
function BookingLeaveManagement({ salonId, onConflictResolved }) {
  const { currentUser } = useSalonAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState(null);

  // Pending leave approvals
  const [pendingLeave, setPendingLeave] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [approvingId, setApprovingId] = useState(null);

  const canApprove = currentUser && ['owner', 'admin', 'manager'].includes(currentUser.role);

  const calculateLeaveDays = (leave) => {
    const start = new Date(leave.startDate.seconds * 1000);
    const end = new Date(leave.endDate.seconds * 1000);
    let days = 0;
    const current = new Date(start);
    while (current <= end) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        if (leave.timeType === 'full') days += 1;
        else if (leave.timeType === 'am' || leave.timeType === 'pm') days += 0.5;
      }
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const calculateTimeOffHours = (leave) => {
    if (leave.type !== 'timeoff') return 0;
    const start = new Date(leave.startDate.seconds * 1000);
    const end = new Date(leave.endDate.seconds * 1000);
    let totalHours = 0;
    const current = new Date(start);
    while (current <= end) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        if (leave.timeType === 'custom' && leave.startTime && leave.endTime) {
          const [sh, sm] = leave.startTime.split(':').map(Number);
          const [eh, em] = leave.endTime.split(':').map(Number);
          totalHours += ((eh * 60 + em) - (sh * 60 + sm)) / 60;
        } else if (leave.timeType === 'am' || leave.timeType === 'pm') {
          totalHours += 4;
        } else if (leave.timeType === 'full') {
          totalHours += 8;
        }
      }
      current.setDate(current.getDate() + 1);
    }
    return totalHours;
  };

  const getStaffLeaveSummary = async (staffId) => {
    try {
      const currentYear = new Date().getFullYear();
      const startOfYear = new Date(currentYear, 0, 1);
      const endOfYear = new Date(currentYear, 11, 31, 23, 59, 59);
      const leaveRef = collection(db, 'leave');
      const leaveQuery = query(leaveRef, where('staffId', '==', staffId), where('salonId', '==', salonId));
      const leaveSnapshot = await getDocs(leaveQuery);
      const summary = { annualLeaveUsed: 0, medicalLeaveUsed: 0, timeOffHours: 0, totalLeaveRecords: 0 };
      leaveSnapshot.forEach(doc => {
        const leave = doc.data();
        if (leave.status === 'approved') {
          const leaveStart = leave.startDate?.seconds ? new Date(leave.startDate.seconds * 1000) : new Date(leave.startDate);
          const leaveEnd = leave.endDate?.seconds ? new Date(leave.endDate.seconds * 1000) : new Date(leave.endDate);
          if (leaveStart <= endOfYear && leaveEnd >= startOfYear) {
            summary.totalLeaveRecords++;
            if (leave.type === 'annual') summary.annualLeaveUsed += calculateLeaveDays(leave);
            else if (leave.type === 'medical') summary.medicalLeaveUsed += calculateLeaveDays(leave);
            else if (leave.type === 'timeoff') summary.timeOffHours += calculateTimeOffHours(leave);
          }
        }
      });
      return summary;
    } catch (error) {
      console.error('Error getting leave summary:', error);
      return { annualLeaveUsed: 0, medicalLeaveUsed: 0, timeOffHours: 0, totalLeaveRecords: 0 };
    }
  };

  const loadStaffWithLeave = useCallback(async () => {
    try {
      setLoading(true);
      const staffData = await getSalonStaff(salonId);
      const staffWithLeave = [];
      for (const s of staffData) {
        const leaveSummary = await getStaffLeaveSummary(s.staffId || s.id);
        staffWithLeave.push({ ...s, leaveSummary });
      }
      setStaff(staffWithLeave);
    } catch (error) {
      console.error('Error loading staff:', error);
    } finally {
      setLoading(false);
    }
  }, [salonId]);

  const loadPendingLeave = useCallback(async () => {
    if (!canApprove) { setLoadingPending(false); return; }
    try {
      setLoadingPending(true);
      const leaveRef = collection(db, 'leave');
      const q = query(leaveRef, where('salonId', '==', salonId));
      const snapshot = await getDocs(q);
      const records = [];
      snapshot.forEach(d => {
        const data = d.data();
        if (data.status === 'pending') records.push({ id: d.id, ...data });
      });
      // Sort oldest first (submitted first = reviewed first)
      records.sort((a, b) => {
        const ca = a.createdAt?.seconds || 0;
        const cb = b.createdAt?.seconds || 0;
        return ca - cb;
      });
      setPendingLeave(records);
    } catch (err) {
      console.error('Error loading pending leave:', err);
    } finally {
      setLoadingPending(false);
    }
  }, [salonId, canApprove]);

  useEffect(() => {
    if (salonId) {
      loadStaffWithLeave();
      loadPendingLeave();
    }
  }, [salonId, loadStaffWithLeave, loadPendingLeave]);

  const handleApprove = async (leaveId) => {
    try {
      setApprovingId(leaveId);
      const leaveDocRef = doc(db, 'leave', leaveId);
      await updateDoc(leaveDocRef, {
        status: 'approved',
        approvedBy: currentUser?.uid || 'manager',
        approvedByName: currentUser?.name || '',
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      // Also mark related notifications as read
      try {
        const notifRef = collection(db, 'salonNotifications');
        // Find notifications for this leave record - match by staffId + salonId + type
        // (simplified: just reload after approval)
      } catch {}
      setPendingLeave(prev => prev.filter(r => r.id !== leaveId));
    } catch (err) {
      console.error('Error approving leave:', err);
    } finally {
      setApprovingId(null);
    }
  };

  const handleStaffSelect = (s) => setSelectedStaff(s);
  const handleBack = () => {
    setSelectedStaff(null);
    loadStaffWithLeave();
    loadPendingLeave();
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading && staff.length === 0) {
    return <div className="loading">Loading staff...</div>;
  }

  // Detail view
  if (selectedStaff) {
    return (
      <div className="staff-editor">
        <button className="cancel-button" onClick={handleBack} style={{ marginBottom: '20px' }}>
          Back to Staff List
        </button>
        <StaffLeaveManager
          salonId={salonId}
          staffId={selectedStaff.staffId || selectedStaff.id}
          staffName={selectedStaff.name}
          leaveAllocation={selectedStaff.leaveAllocation || { annualLeave: 20, medicalLeave: 10 }}
          submittedByRole={currentUser?.role || 'admin'}
          onUpdate={handleBack}
          onConflictResolved={onConflictResolved}
        />
      </div>
    );
  }

  return (
    <div className="staff-editor">
      <h2>Leave Management</h2>

      {/* ---- Pending Approvals ---- */}
      {canApprove && (
        <div className="pending-leave-section">
          <h3>
            Pending Approvals
            {pendingLeave.length > 0 && (
              <span className="pending-leave-badge">{pendingLeave.length}</span>
            )}
          </h3>
          {loadingPending ? (
            <p style={{ color: '#888', fontSize: '0.9rem' }}>Loading...</p>
          ) : pendingLeave.length === 0 ? (
            <p style={{ color: '#888', fontSize: '0.9rem' }}>No pending leave requests.</p>
          ) : (
            pendingLeave.map(record => {
              const typeLabel = record.type === 'annual' ? 'Annual Leave' : record.type === 'medical' ? 'Medical Leave' : 'Time Off';
              const timeLabel = record.timeType === 'full' ? 'Full Day' : record.timeType === 'am' ? 'Morning (AM)' : record.timeType === 'pm' ? 'Afternoon (PM)' : `${record.startTime || ''} – ${record.endTime || ''}`;
              return (
                <div key={record.id} className="pending-leave-card">
                  <div className="pending-leave-info">
                    <div className="staff-name">{record.staffName || record.staffId}</div>
                    <div className="leave-detail">{typeLabel} · {timeLabel}</div>
                    <div className="leave-detail">{formatDate(record.startDate)} – {formatDate(record.endDate)}</div>
                    {record.reason && <div className="leave-reason">"{record.reason}"</div>}
                  </div>
                  <button
                    className="approve-leave-btn"
                    onClick={() => handleApprove(record.id)}
                    disabled={approvingId === record.id}
                  >
                    {approvingId === record.id ? 'Approving...' : 'Approve'}
                  </button>
                </div>
              );
            })
          )}
          <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '24px 0 20px' }} />
        </div>
      )}

      {/* ---- Staff leave list ---- */}
      <p style={{ color: '#666', marginBottom: '20px', fontFamily: 'Wix Madefor Text, sans-serif' }}>
        Select a staff member to view and manage their leave records.
      </p>

      {staff.length === 0 ? (
        <div className="no-staff">
          <p>No staff found. Please add staff in the Core module first.</p>
        </div>
      ) : (
        <div className="staff-list">
          <div className="staff-items">
            {staff.map(s => (
              <div
                key={s.staffId || s.id}
                className={`staff-item ${!s.isActive ? 'inactive' : ''}`}
                onClick={() => handleStaffSelect(s)}
                style={{ cursor: 'pointer' }}
              >
                <div className="staff-content">
                  <div className="staff-header">
                    <div className="staff-photo">
                      {s.photoURL ? (
                        <img
                          src={s.photoURL}
                          alt={s.name}
                          onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <div className="image-placeholder" style={s.photoURL ? { display: 'none' } : undefined}>
                        {s.name ? s.name.charAt(0).toUpperCase() : '?'}
                      </div>
                    </div>
                    <div className="staff-info">
                      <h3 className="staff-name">
                        {s.name}
                        {!s.isActive && <span className="inactive-badge">Inactive</span>}
                      </h3>
                      {s.title && <p className="staff-title">{s.title}</p>}
                    </div>
                  </div>
                  <div className="staff-leave-summary">
                    <strong>Leave Summary ({new Date().getFullYear()}):</strong>
                    <div className="leave-stats">
                      <span>Annual: {s.leaveSummary?.annualLeaveUsed || 0}/{s.leaveAllocation?.annualLeave || 20} days</span>
                      <span>Medical: {s.leaveSummary?.medicalLeaveUsed || 0}/{s.leaveAllocation?.medicalLeave || 10} days</span>
                      <span>Time Off: {s.leaveSummary?.timeOffHours || 0} hours</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingLeaveManagement;
